import { TFunction } from 'react-i18next'
import { Options } from 'types/BankAccountsDetails'
import { BankTransferType } from 'types/beneficiaries'
import { WalletType } from 'types/types'

export const getLocalBankDetails = (
  options: Options,
  wallet: WalletType,
  currency: string,
  isSegregated: boolean,
  t: TFunction<'translation', undefined>,
  isDepositsV2: boolean
) => {
  let details = {}

  switch (currency) {
    case 'EUR':
      details = getEurDetails(options, wallet, t, isDepositsV2)
      break
    case 'GBP':
      details = getGbpDetails(options, wallet, t, isDepositsV2)
      break
    case 'ZAR':
      details = getZarDetails(options, wallet, t, isDepositsV2)
      break
    case 'NGN':
      details = getNgnDetails(options, wallet, t)
      break
    case 'USD':
      details = getUsdDetails(options, wallet, t, isDepositsV2)
      break
  }

  if (isSegregated) {
    if (options?.reference) {
      details[t('referenceNumber')] = options.reference
    } else {
      delete details[t('referenceNumber')]
    }
  }

  return details
}

const getEurDetails = (
  options: Options,
  wallet,
  t: TFunction<'translation', undefined>,
  isDepositsV2: boolean
) => {
  if (isDepositsV2) {
    return {
      [t('referenceNumber')]: wallet.address || '-',
      ...(options?.accountHolderName && { [t('accountName')]: options.accountHolderName }),
      ...(options?.accountNumber && { [t('accountNumber')]: options.accountNumber }),
      ...(options?.bankCode && { [t('bankCode')]: options.bankCode }),
    }
  } else {
    return {
      [t('referenceNumber')]: wallet.address || '-',
      ...(options?.accountHolderName && { [t('accountName')]: options.accountHolderName }),
      ...(options?.iban && { IBAN: options.iban }),
      ...(options?.bic && { BIC: options.bic }),
    }
  }
}

const getGbpDetails = (
  options: Options,
  wallet,
  t: TFunction<'translation', undefined>,
  isDepositsV2: boolean
) => {
  if (isDepositsV2) {
    return {
      [t('referenceNumber')]: wallet.address || '-',
      ...(options?.accountHolderName && { [t('accountName')]: options.accountHolderName }),
      ...(options?.accountNumber && { [t('accountNumber')]: options.accountNumber }),
      ...(options?.bankCode && { [t('bankCode')]: options.bankCode }),
    }
  } else {
    return {
      [t('referenceNumber')]: wallet.address || '-',
      ...(options?.accountHolderName && { [t('accountName')]: options.accountHolderName }),
      ...(options?.ukSortCode && { [t('sortCode')]: options.ukSortCode }),
      ...(options?.ukAccountNumber && { [t('accountNumber')]: options.ukAccountNumber }),
    }
  }
}

const getZarDetails = (
  options: Options,
  wallet,
  t: TFunction<'translation', undefined>,
  isDepositsV2: boolean
) => {
  if (isDepositsV2) {
    return {
      [t('referenceNumber')]: wallet.address || '-',
      ...(options?.accountHolderName && { [t('accountName')]: options.accountHolderName }),
      ...(options?.accountNumber && { [t('accountNumber')]: options.accountNumber }),
      ...(options?.bankCode && { [t('bankCode')]: options.bankCode }),
      ...(options?.bankName && { [t('bankName')]: options.bankName }),
      ...(options?.accountType && { [t('accountType')]: options.accountType }),
    }
  } else {
    return {
      [t('referenceNumber')]: wallet.address || '-',
      ...(options?.accountHolderName && { [t('accountName')]: options.accountHolderName }),
      ...(options?.accountNumber && { [t('accountNumber')]: options.accountNumber }),
      ...(options?.routingNumber && { [t('branchCode')]: options.routingNumber }),
      ...(options?.bankName && { [t('bankName')]: options.bankName }),
      ...(options?.accountType && { [t('accountType')]: options.accountType }),
    }
  }
}

const getUsdDetails = (
  options: Options,
  wallet,
  t: TFunction<'translation', undefined>,
  isDepositsV2: boolean
) => {
  return {
    [t('referenceNumber')]: wallet?.address || '-',
    ...(options?.accountHolderName && { [t('accountName')]: options.accountHolderName }),
    ...(options?.usdBankRoutingNumber && {
      [t('bankRoutingNumber')]: options.usdBankRoutingNumber,
    }),
    ...(!isDepositsV2 &&
      options?.usdAccountNumber && { [t('accountNumber')]: options.usdAccountNumber }),
    ...(isDepositsV2 && options?.accountNumber && { [t('accountNumber')]: options.accountNumber }),
    ...(options?.bankName && { [t('bankName')]: options.bankName }),
    ...(options?.bankAddress && { [t('bankAddress')]: options.bankAddress }),
    ...(options?.bankCity && { [t('bankCity')]: options.bankCity }),
    ...(options?.bankState && { [t('bankState')]: options.bankState }),
    ...(options?.bankCountry && { [t('bankCountry')]: options.bankCountry }),
    ...(options?.bankPostalCode && { [t('bankPostalCode')]: options.bankPostalCode }),
  }
}

const getNgnDetails = (options: Options, wallet, t: TFunction<'translation', undefined>) => {
  return {
    [t('referenceNumber')]: wallet?.address || '-',
    ...(options?.accountHolderName && { [t('accountName')]: options.accountHolderName }),
    ...(options?.accountNumber && { [t('accountNumber')]: options.accountNumber }),
    ...(options?.bankName && { [t('bankName')]: options.bankName }),
  }
}

export const getSwiftBankDetails = (
  options: Options,
  t: TFunction<'translation', undefined>,
  isDepositsV2: boolean,
  shouldFetchReference = true
): {
  beneficiaryDetails: { [property: string]: string }
  beneficiaryBankDetails: { [property: string]: string }
} => {
  const paymentReference = `${options?.metaData?.referencePrefix || ''}${
    options?.referenceNumber || ''
  }`
  return {
    beneficiaryDetails: {
      ...(options?.beneficiaryName && { [t('beneficiaryName')]: options?.beneficiaryName }),
      ...(options?.beneficiaryAddress?.addressLine && {
        [t('beneficiaryAddress')]: options?.beneficiaryAddress?.addressLine,
      }),
      ...(options?.beneficiaryAddress?.addressLine && {
        [t('city')]: options?.beneficiaryAddress?.city,
      }),
      ...(options?.beneficiaryAddress?.countryCode && {
        [t('country')]: options?.beneficiaryAddress?.countryCode,
      }),
      ...(options?.beneficiaryAddress?.postalCode && {
        [t('zip')]: options?.beneficiaryAddress?.postalCode,
      }),
    },
    beneficiaryBankDetails: {
      ...(isDepositsV2 &&
        options?.accountNumber && { [t('accountNumber')]: options.accountNumber }),
      ...(isDepositsV2 && options?.bankCode && { [t('bankCode')]: options.bankCode }),
      // EUR
      ...(!isDepositsV2 && options?.iban && { [t('accountNumber')]: options.iban }),
      ...(!isDepositsV2 && options?.bic && { [t('bic')]: options.bic }),
      // USD
      ...(!isDepositsV2 &&
        options?.usdAccountNumber && { [t('accountNumber')]: options.usdAccountNumber }),
      ...(!isDepositsV2 &&
        options?.usdBankRoutingNumber && {
          [t('abaRoutingNumber')]: options.usdBankRoutingNumber,
        }),
      ...(options?.bankName && { [t('bankName')]: options.bankName }),
      ...(options?.bankAddress?.addressLine && { [t('address')]: options.bankAddress.addressLine }),
      ...(options?.bankAddress?.countryCode && { [t('country')]: options.bankAddress.countryCode }),
      ...(shouldFetchReference &&
        paymentReference && { [t('paymentReference')]: paymentReference }),
    },
  }
}

export const getPaymentMethodTitle = (
  paymentMethod: string,
  t: TFunction<'translation', undefined>
): string => {
  if (paymentMethod === BankTransferType.SWIFT) {
    return t('internationalTransfer')
  } else {
    return t('localTransfer', { paymentMethod })
  }
}
